import React, { useEffect, useState } from "react";
import { showAlert } from "../../alertService";
import { db } from "../../firebase";
import { BITACORA_ACTIONS, logBitacora } from "../../utils/bitacora";
import { Alert } from "../alert";
import { Button } from "../button";
import { ACCENT, BTN_PRIMARIO, BTN_PRIMARIO_SM, BTN_SECUNDARIO_SM } from "./shared";
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from "@heroicons/react/20/solid";

// ---------- Notas tab (timeline) ----------
const NotasTab = ({ proveedor, venueId, userInfo, user, soloLectura = false }) => {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevoTexto, setNuevoTexto] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    if (!venueId || !proveedor?.id) return;
    const q = query(
      collection(db, "venues", venueId, "proveedores", proveedor.id, "notas"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Notas snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId, proveedor?.id]);

  const handleAdd = async () => {
    const texto = nuevoTexto.trim();
    if (!texto || submitting) return;
    setSubmitting(true);
    try {
      const ref = collection(
        db,
        "venues",
        venueId,
        "proveedores",
        proveedor.id,
        "notas"
      );
      const docRef = await addDoc(ref, {
        texto,
        authorId: user?.uid || null,
        authorName: userInfo?.name || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_NOTA_CREATE,
          entity: "proveedor_nota",
          entityId: docRef.id,
          entityName: proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
          },
        },
        userInfo,
        user
      );
      setNuevoTexto("");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (id) => {
    const texto = editDraft.trim();
    if (!texto) {
      setEditingId(null);
      return;
    }
    try {
      await updateDoc(
        doc(db, "venues", venueId, "proveedores", proveedor.id, "notas", id),
        { texto, updatedAt: serverTimestamp() }
      );
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_NOTA_EDIT,
          entity: "proveedor_nota",
          entityId: id,
          entityName: proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
          },
        },
        userInfo,
        user
      );
      setEditingId(null);
      setEditDraft("");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    try {
      await deleteDoc(
        doc(
          db,
          "venues",
          venueId,
          "proveedores",
          proveedor.id,
          "notas",
          deleteCandidate.id
        )
      );
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_NOTA_DELETE,
          entity: "proveedor_nota",
          entityId: deleteCandidate.id,
          entityName: proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
          },
        },
        userInfo,
        user
      );
      setDeleteCandidate(null);
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const formatNoteDate = (ts) => {
    if (!ts?.toDate) return "";
    return ts.toDate().toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4">
      {/* Legacy notes (from old single-textarea field) */}
      {proveedor.notasInternas && proveedor.notasInternas.trim() && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
            Notas antiguas (legacy)
          </div>
          <div className="text-sm text-zinc-800 whitespace-pre-wrap">
            {proveedor.notasInternas}
          </div>
        </div>
      )}

      {/* Compose — no se ofrece en solo lectura (P-FRA-13) */}
      {!soloLectura && (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-4 shadow-sm">
        <textarea
          value={nuevoTexto}
          onChange={(e) => setNuevoTexto(e.target.value)}
          placeholder="Agrega una nota... (acuerdos, anécdotas, recordatorios, etc.)"
          rows={3}
          className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!nuevoTexto.trim() || submitting}
            className={BTN_PRIMARIO}
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            {submitting ? "Agregando..." : "Agregar nota"}
          </button>
        </div>
      </div>
      )}

      {/* Feed */}
      {loading ? (
        <div className="py-8 text-center text-zinc-400">Cargando...</div>
      ) : notas.length === 0 ? (
        <div className="py-8 text-center text-zinc-400 italic">
          {soloLectura ? "Aún no hay notas." : "Aún no hay notas. Agrega la primera."}
        </div>
      ) : (
        <div className="space-y-2">
          {notas.map((n) => {
            const isEditing = editingId === n.id;
            return (
              <div
                key={n.id}
                className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-4 shadow-sm"
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      autoFocus
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft("");
                        }}
                        className={BTN_SECUNDARIO_SM}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(n.id)}
                        className={BTN_PRIMARIO_SM}
                        style={{ backgroundColor: ACCENT }}
                      >
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-800 whitespace-pre-wrap">
                          {n.texto}
                        </div>
                        <div className="mt-2 text-xs text-zinc-400">
                          {n.authorName || "Anónimo"} ·{" "}
                          {formatNoteDate(n.createdAt)}
                          {n.updatedAt &&
                            n.createdAt &&
                            n.updatedAt.toMillis &&
                            n.createdAt.toMillis &&
                            n.updatedAt.toMillis() - n.createdAt.toMillis() >
                              1000 &&
                            ` · editada ${formatNoteDate(n.updatedAt)}`}
                        </div>
                      </div>
                      {!soloLectura && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(n.id);
                            setEditDraft(n.texto || "");
                          }}
                          className="text-zinc-400 hover:text-zinc-600 p-1"
                          title="Editar"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(n)}
                          className="text-zinc-400 hover:text-red-500 p-1"
                          title="Eliminar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Alert
        open={!!deleteCandidate}
        onClose={() => setDeleteCandidate(null)}
      >
        <div className="font-semibold mb-2">¿Eliminar esta nota?</div>
        <div className="mb-4 text-sm text-zinc-600">
          Esta acción no se puede deshacer.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

export default NotasTab;
